import React, { useState, useEffect } from 'react'; 
import { Link, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Hammer, Sparkles, CheckCircle, Award, Shield, Phone, Menu, X } from 'lucide-react';
import './Services.css';
import './Home.css'; // Import shared CSS for Header and Footer

// --- Shared Data (remains the same) ---
const navItems = [
    { id: 'home', label: 'Home', path: '/' }, 
    { id: 'about', label: 'About', path: '/about' },
    { id: 'services', label: 'Services', path: '/services' },
    { id: 'projects', label: 'Projects', path: '/projects' },
    { id: 'contact', label: 'Contact', path: '/contact' }
];

// --- Helper Component for Header content (remains the same) ---
const HeaderContent = ({ isMobileMenuOpen, setIsMobileMenuOpen }) => {
    const location = useLocation();
    const getLinkClass = (path) => location.pathname === path ? 'active' : '';

    return (
        <header className="header scrolled"> 
            <div className="header-content">
                <img src="/assets/Construction Logo (4).png" alt="S&L Construction Logo" className="header-logo" />
                <Link to="/" className="logo">
                    S&L Construction, Inc.
                </Link>

                <nav className="nav-desktop">
                    {navItems.map(item => (
                        <Link 
                            key={item.id}
                            to={item.path} 
                            className={`nav-link ${getLinkClass(item.path)}`}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <a href="tel:+13126077476" className="header-phone">
                    <Phone size={18} />
                    (312) 607-7476
                </a>

                <button
                    className="mobile-menu-button"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                >
                    {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <div className={`nav-mobile ${isMobileMenuOpen ? 'open' : ''}`}>
                {navItems.map(item => (
                    <Link 
                        key={item.id}
                        to={item.path}
                        className={`nav-link-mobile ${getLinkClass(item.path)}`}
                        onClick={() => setIsMobileMenuOpen(false)} 
                    >
                        {item.label}
                    </Link>
                ))}
                <a href="tel:+13126077476" className="nav-phone-mobile">
                    <Phone size={18} />
                    (312) 607-7476
                </a>
            </div>
        </header>
    );
};
// --- End Shared Header Content ---

// --- Helper Component for Footer Content (remains the same) ---
const FooterContent = () => (
    <footer className="footer-section">
        <div className="footer-content">
            <p className="footer-copy">© 2025 S&L Construction, Inc. | All Rights Reserved</p>
            <a href="tel:+13126077476" className="footer-phone">
                <Phone size={16} />
                Call Us: (312) 607-7476
            </a>
        </div>
    </footer>
);
// --- End Helper Component for Footer Content ---


// --- UPDATED Image Data with Certificate Links ---
const carouselItems = [
    { id: 1, text: 'Licensed General Contractor', imgUrl: 'https://images.besttemplates.com/wp-content/uploads/2024/06/Business-Certificate-Design-scaled.jpg' },
    { id: 2, text: 'Certified Lead Renovator', imgUrl: 'https://graphicsfamily.com/wp-content/uploads/edd/2021/01/Company-certificate-template-elegant-design-vector-illustration-scaled.jpg' },
    { id: 3, text: '20+ Years Expertise', imgUrl: 'https://cdn.awardbox.com/images/Vintage2_thumb_hero/certificate-of-business-template-classic2-blue-stamp.jpg' },
    { id: 4, text: 'High-End Quality Assurance', imgUrl: 'https://aavana.in/blog/wp-content/uploads/2020/02/certificate.jpg' },
    { id: 5, text: 'Trusted & Fully Insured', imgUrl: 'https://d1csarkz8obe9u.cloudfront.net/posterpreviews/modern-corporate-certificate-template-design-33e8f36039aa0bd471678c8ddbd7a9a8_screen.jpg?ts=1630937989' }
];
const totalItems = carouselItems.length;

const Services = () => {
    const isVisible = true; 
    const [activeIndex, setActiveIndex] = useState(0); 
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const goToNext = () => {
        setActiveIndex((prevIndex) => (prevIndex + 1) % totalItems);
    };
    
    useEffect(() => {
        const autoScroll = setInterval(() => {
            goToNext();
        }, 5000); 

        return () => clearInterval(autoScroll);
    }, [activeIndex]); 

    const goToPrev = () => {
        setActiveIndex((prevIndex) => (prevIndex - 1 + totalItems) % totalItems);
    };

    const getCardClass = (index) => {
        if (index === activeIndex) return 'active';
        
        // This calculates the indices relative to the active one for a smooth loop
        const prevIndex = (activeIndex - 1 + totalItems) % totalItems;
        const nextIndex = (activeIndex + 1) % totalItems;

        if (index === prevIndex) return 'prev';
        if (index === nextIndex) return 'next';
        
        return 'off-screen'; 
    };

    const services = [
        { icon: <Hammer size={40} />, title: 'Custom Remodeling', description: 'From kitchens and bathrooms to full-scale renovations, we create spaces that reflect your taste, needs, and lifestyle.' },
        { icon: <HomeIcon size={40} />, title: 'New Builds', description: 'We manage every stage of custom home construction, delivering modern, tailored homes built to last.' },
        { icon: <Sparkles size={40} />, title: 'Luxury Finishes & Additions', description: "Whether it's a bespoke addition, unique material, or high-end finish, we bring your vision to life with meticulous detail." }
    ];

    const reasons = [
        { icon: <CheckCircle size={24} />, title: 'Bespoke Solutions', description: 'Every project is custom-tailored.' },
        { icon: <Award size={24} />, title: 'Quality Craftsmanship', description: 'Attention to detail in every build.' },
        { icon: <Shield size={24} />, title: 'End-to-End Service', description: 'From design to delivery, we manage it all.' },
        { icon: <CheckCircle size={24} />, title: 'Licensed & Certified', description: 'Backed by recognized certifications and 20+ years of expertise.' }
    ];

    return (
        <div className="page-wrapper">
            {/* 1. HEADER SECTION */}
            <HeaderContent 
                isMobileMenuOpen={isMobileMenuOpen} 
                setIsMobileMenuOpen={setIsMobileMenuOpen} 
            />

            {/* 2. MAIN CONTENT */}
            <main>
                <div className="services-container">
                    <div className="services-content">
                        
                        <div className={`section-header ${isVisible ? 'animate-in' : ''}`}>
                            <span className="section-label">Our Core Services</span>
                            <h2 className="section-title">What We Do Best</h2>
                        </div>

                        <div className="services-grid">
                            {services.map((service, index) => (
                                <div 
                                    key={index} 
                                    className={`service-card ${isVisible ? 'fade-in' : ''}`}
                                    style={{ animationDelay: `${index * 0.2}s` }}
                                >
                                    <div className="service-icon">{service.icon}</div>
                                    <h3 className="service-title">{service.title}</h3>
                                    <p className="service-description">{service.description}</p>
                                </div>
                            ))}
                        </div>

                        <div className={`why-choose-section ${isVisible ? 'slide-in' : ''}`}>
                            <h2 className="why-title">Why Clients Choose S&L Construction</h2>
                            <div className="reasons-grid">
                                {reasons.map((reason, index) => (
                                    <div 
                                        key={index} 
                                        className="reason-card"
                                        style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                                    >
                                        <div className="reason-icon">{reason.icon}</div>
                                        <div className="reason-content">
                                            <h4 className="reason-title">{reason.title}</h4>
                                            <p className="reason-description">{reason.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* --- Certification Section with Rotational Carousel --- */}
                        <div className={`certification-section ${isVisible ? 'fade-in-up' : ''}`}>
                            <h2 className="cert-title">Licensed, Certified, and Trusted</h2>
                            <p className="cert-text">
                                S&L Construction, Inc. is a licensed general contractor, certified lead renovator, 
                                and trusted name in high-end construction for over two decades. Our credentials 
                                guarantee professionalism, compliance, and quality workmanship.
                            </p>

                            <div className="carousel-wrapper">
                                
                                {/* Manual navigation buttons (Adjusted in CSS) */}
                                <button className="nav-button left" onClick={goToPrev}>&lt;</button>
                                
                                <div className="cert-carousel">
                                    {carouselItems.map((item, index) => (
                                        <div 
                                            key={item.id} 
                                            className={`cert-item ${getCardClass(index)}`} 
                                        >
                                            <img src={item.imgUrl} alt={item.text} className="cert-image" />
                                            <p className="cert-label-text">{item.text}</p>
                                        </div>
                                    ))}
                                </div>
                                
                                <button className="nav-button right" onClick={goToNext}>&gt;</button>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            
            {/* 3. FOOTER SECTION */}
            <FooterContent />
        </div>
    );
};

export default Services;